import { ApolloCache, gql } from '@apollo/client';
import { categoryFragment } from '../fragments/category';
import { goalFragment } from '../fragments/goal';
import { questFragment } from '../fragments/quest';
import {
    GetQuestsByCategoryQuery,
    GetQuestsByCategoryQueryVariables,
    GetQuestsQuery,
    GetQuestsQueryVariables,
    QuestFieldsFragment,
    QuestStatus,
    UpdateQuestMutation,
} from '../generated/graphql';
import { getQuestsByCategoryQuery, getQuestsQuery } from '../queries/quest';
import { forEachPrimaryQuestsQuery } from './cacheHelpers';

export const updateQuestMutation = gql`
    ${categoryFragment}
    ${goalFragment}
    ${questFragment}

    mutation UpdateQuest(
        $questId: String!
        $name: String!
        $description: String!
        $repeat: RepeatCreateInput
        $dueDate: DateTimeISO
        $timing: Boolean!
        $timedInterval: Int!
        $timerValueInMs: Int!
        $categoryId: String!
        $difficulty: QuestDifficulty!
        $goalId: String
        $status: QuestStatus
        $subTasks: [SubTaskCreateInput!]
    ) {
        updateQuest(
            data: {
                id: $questId
                name: $name
                description: $description
                categoryId: $categoryId
                difficulty: $difficulty
                goalId: $goalId
                repeat: $repeat
                dueDate: $dueDate
                timing: $timing
                timedInterval: $timedInterval
                timerValueInMs: $timerValueInMs
                status: $status
                subTasks: $subTasks
            }
        ) {
            ...QuestFields
            category {
                ...CategoryFields
            }
            goal {
                ...GoalFields
            }
        }
    }
`;

export function updateQuestCache(cache: ApolloCache<any>, { data }: { data?: UpdateQuestMutation | null }, oldCategoryId?: string) {
    if (!data?.updateQuest) return;

    const updatedQuest = data.updateQuest;

    if (oldCategoryId && updatedQuest.categoryId !== oldCategoryId) {
        const oldQuest = cache.readFragment<QuestFieldsFragment>({
            id: cache.identify({ __typename: 'Quest', id: updatedQuest.id }),
            fragment: questFragment,
            fragmentName: 'QuestFields',
        });

        const isCompleted = oldQuest?.status === QuestStatus.Done;

        cache.modify({
            id: cache.identify({ __typename: 'Category', id: oldCategoryId }),
            fields: {
                questProgressTotal(existingTotal = 0) {
                    return Math.max(0, existingTotal - 1);
                },
                questProgressCompleted(existingCompleted = 0) {
                    return isCompleted ? Math.max(0, existingCompleted - 1) : existingCompleted;
                },
            },
        });
    }

    forEachPrimaryQuestsQuery(cache, (vars, existingData) => {
        const questExists = existingData.quests.some(q => q.id === updatedQuest.id);

        if (!questExists) {
            cache.writeQuery<GetQuestsQuery, GetQuestsQueryVariables>({
                query: getQuestsQuery,
                variables: vars,
                data: {
                    ...existingData,
                    quests: [...existingData.quests, updatedQuest as any],
                },
            });
        }
    });

    const currentCategoryData = cache.readQuery<GetQuestsByCategoryQuery, GetQuestsByCategoryQueryVariables>({
        query: getQuestsByCategoryQuery,
        variables: { categoryId: updatedQuest.categoryId },
    });
    if (currentCategoryData && currentCategoryData.questsByCategory) {
        const existingQuests = currentCategoryData.questsByCategory;
        const questIndex = existingQuests.findIndex(q => q.id === updatedQuest.id);
        if (questIndex !== -1) {
            const updatedQuests = [...existingQuests];
            updatedQuests[questIndex] = updatedQuest;
            cache.writeQuery<GetQuestsByCategoryQuery, GetQuestsByCategoryQueryVariables>({
                query: getQuestsByCategoryQuery,
                variables: { categoryId: updatedQuest.categoryId },
                data: { ...currentCategoryData, questsByCategory: updatedQuests },
            });
        }
    }

    if (oldCategoryId && updatedQuest.categoryId !== oldCategoryId) {
        const oldCategoryData = cache.readQuery<GetQuestsByCategoryQuery, GetQuestsByCategoryQueryVariables>({
            query: getQuestsByCategoryQuery,
            variables: { categoryId: oldCategoryId },
        });
        if (oldCategoryData && oldCategoryData.questsByCategory) {
            const existingQuests = oldCategoryData.questsByCategory;
            const filteredQuests = existingQuests.filter(q => q.id !== updatedQuest.id);
            cache.writeQuery<GetQuestsByCategoryQuery, GetQuestsByCategoryQueryVariables>({
                query: getQuestsByCategoryQuery,
                variables: { categoryId: oldCategoryId },
                data: { ...oldCategoryData, questsByCategory: filteredQuests },
            });
        }
    }
}
